/* ============================================================================
   Product Video Reels — JavaScript
   Two custom elements:
     1. <product-reel-widget>  — floating video preview widget
     2. <product-reel-modal>   — full-screen reel viewer
   ============================================================================ */

(() => {
  'use strict';

  /* ------------------------------------------------------------------
     Widget: Floating video preview
     ------------------------------------------------------------------ */
  if (!customElements.get('product-reel-widget')) {
    customElements.define('product-reel-widget', class extends HTMLElement {
      connectedCallback() {
        /* Move to body to avoid stacking context / containing block issues */
        if (this.parentElement !== document.body) {
          // Track original position to handle SPA page transitions
          this._placeholder = document.createComment('reel-widget-placeholder');
          this.parentElement.insertBefore(this._placeholder, this);

          // Clean up old widgets from previous pages
          document.querySelectorAll('product-reel-widget').forEach(w => {
            if (w !== this) w.remove();
          });

          document.body.appendChild(this);

          // Self-destruct if the page content changes (SPA navigation)
          this._spaCheck = setInterval(() => {
            if (!this._placeholder.isConnected) {
              clearInterval(this._spaCheck);
              this.remove();
            }
          }, 500);
          return;
        }

        /* Check if dismissed this session */
        if (sessionStorage.getItem('reel-widget-dismissed') === this.dataset.productHandle) {
          this.remove();
          return;
        }

        this.video = this.querySelector('.reel-widget__video');
        this.closeBtn = this.querySelector('.reel-widget__close');
        this.progressFill = this.querySelector('.reel-widget__progress-fill');

        if (this.video) {
          // Force strict mobile autoplay properties immediately (synchronous with page load)
          this.video.muted = true;
          this.video.defaultMuted = true;
          this.video.playsInline = true;
          
          if (this.video.dataset.src) {
            this.video.src = this.video.dataset.src;
          }
          
          let retryCount = 0;
          const forcePlay = () => {
            const playPromise = this.video.play();
            if (playPromise !== undefined) {
              playPromise.then(() => {
                this.setAttribute('data-playing', 'true');
              }).catch((err) => {
                if (retryCount < 20) {
                  // Keep trying even on NotAllowedError in case it's a DOM timing issue
                  retryCount++;
                  setTimeout(forcePlay, 100);
                } else {
                  console.log('[Reels] Autoplay definitively blocked:', err.name);
                  this.setAttribute('data-playing', 'false');
                }
              });
            } else {
              this.setAttribute('data-playing', 'true');
            }
          };
          
          forcePlay();

          /* Show widget only when the video actually has data, preventing a black box */
          const showWidget = () => this.classList.add('reel-widget--visible');
          if (this.video.readyState >= 2) { // HAVE_CURRENT_DATA
            showWidget();
          } else {
            this.video.addEventListener('loadeddata', showWidget, { once: true });
            this.video.addEventListener('playing', showWidget, { once: true });
            setTimeout(showWidget, 4000); // Failsafe fallback
          }
        } else {
          // Fallback for Instagram iframe embeds
          setTimeout(() => {
            this.classList.add('reel-widget--visible');
          }, 800);
        }

        /* Looping progress bar tracking */
        if (this.video && this.progressFill) {
          this.video.addEventListener('timeupdate', () => {
            const pct = this.video.duration
              ? (this.video.currentTime / this.video.duration) * 100
              : 0;
            this.progressFill.style.width = pct + '%';
          });
        }

        /* Click → open full-screen modal */
        this.addEventListener('click', (e) => {
          if (e.target.closest('.reel-widget__close')) return;
          const modal = document.querySelector('product-reel-modal');
          if (modal) modal.open();
        });

        /* Close → dismiss widget for this session */
        if (this.closeBtn) {
          this.closeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            this.classList.remove('reel-widget--visible');
            sessionStorage.setItem('reel-widget-dismissed', this.dataset.productHandle);
            setTimeout(() => this.remove(), 400);
          });
        }
      }
      disconnectedCallback() {
        if (this._spaCheck) clearInterval(this._spaCheck);
      }
    });
  }

  /* ------------------------------------------------------------------
     Modal: Full-screen reel viewer
     ------------------------------------------------------------------ */
  if (!customElements.get('product-reel-modal')) {
    customElements.define('product-reel-modal', class extends HTMLElement {
      connectedCallback() {
        /* Move to body to avoid stacking context / containing block issues */
        if (this.parentElement !== document.body) {
          // Track original position
          this._placeholder = document.createComment('reel-modal-placeholder');
          this.parentElement.insertBefore(this._placeholder, this);

          // Clean up old modals
          document.querySelectorAll('product-reel-modal').forEach(m => {
            if (m !== this) m.remove();
          });

          document.body.appendChild(this);

          // Self-destruct if page changes
          this._spaCheck = setInterval(() => {
            if (!this._placeholder.isConnected) {
              clearInterval(this._spaCheck);
              this.remove();
              document.body.classList.remove('reel-modal-open');
              document.body.style.overflow = '';
            }
          }, 500);
          return;
        }

        this.video       = this.querySelector('.reel-modal__video');
        this.muteBtn     = this.querySelector('.reel-modal__mute-btn');
        this.closeBtn    = this.querySelector('.reel-modal__close-btn');
        this.shareBtn    = this.querySelector('.reel-modal__share-btn');
        this.overlay     = this.querySelector('.reel-modal__overlay');

        this.prevBtn     = this.querySelector('.reel-modal__nav--prev');
        this.nextBtn     = this.querySelector('.reel-modal__nav--next');
        this.videoWrap   = this.querySelector('.reel-modal__video-wrap');
        this.productsContainer = this.querySelector('.reel-modal__products');

        /* Parse reel data */
        const dataEl = document.getElementById(`ReelData-${this.dataset.sectionId}`);
        if (!dataEl) return;
        try {
          this.reelData = JSON.parse(dataEl.textContent);
        } catch (e) {
          console.error('[Reels] Failed to parse reel data:', e);
          return;
        }

        this.videos = this.reelData.videos || [];
        this.currentIndex = 0;
        this.isMuted = false;
        this._animFrame = null;
        this._variantsFetched = false;

        /* Bind events */
        if (this.closeBtn) this.closeBtn.addEventListener('click', () => this.close());
        if (this.overlay) this.overlay.addEventListener('click', () => this.close());
        if (this.muteBtn) this.muteBtn.addEventListener('click', () => this.toggleMute());
        if (this.shareBtn) {
          this.shareBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.share();
          });
        }
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.navigate(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.navigate(1));

        /* Keyboard */
        this._onKeydown = (e) => {
          if (!this.hasAttribute('open')) return;
          if (e.key === 'Escape') this.close();
          if (e.key === 'ArrowLeft') this.navigate(-1);
          if (e.key === 'ArrowRight') this.navigate(1);
          if (e.key === 'm' || e.key === 'M') this.toggleMute();
        };
        document.addEventListener('keydown', this._onKeydown);

        /* Touch swipe */
        this._touchStartX = 0;
        this._touchStartY = 0;
        if (this.videoWrap) {
          this.videoWrap.addEventListener('touchstart', (e) => {
            this._touchStartX = e.touches[0].clientX;
            this._touchStartY = e.touches[0].clientY;
          }, { passive: true });
          this.videoWrap.addEventListener('touchend', (e) => {
            const dx = e.changedTouches[0].clientX - this._touchStartX;
            const dy = e.changedTouches[0].clientY - this._touchStartY;
            if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 50) {
              this.navigate(dx < 0 ? 1 : -1);
            }
          }, { passive: true });
        }

        /* Mouse drag to scroll for products container */
        if (this.productsContainer) {
          let isDown = false;
          let startX;
          let scrollLeft;

          this.productsContainer.addEventListener('mousedown', (e) => {
            isDown = true;
            this.productsContainer.style.cursor = 'grabbing';
            this.productsContainer.style.scrollSnapType = 'none'; // disable snap while dragging
            startX = e.pageX - this.productsContainer.offsetLeft;
            scrollLeft = this.productsContainer.scrollLeft;
          });
          this.productsContainer.addEventListener('mouseleave', () => {
            isDown = false;
            this.productsContainer.style.cursor = '';
            this.productsContainer.style.scrollSnapType = '';
          });
          this.productsContainer.addEventListener('mouseup', () => {
            isDown = false;
            this.productsContainer.style.cursor = '';
            this.productsContainer.style.scrollSnapType = '';
          });
          this.productsContainer.addEventListener('mousemove', (e) => {
            if (!isDown) return;
            e.preventDefault();
            const x = e.pageX - this.productsContainer.offsetLeft;
            const walk = (x - startX) * 2; // scroll-fast
            this.productsContainer.scrollLeft = scrollLeft - walk;
          });
        }

        /* Video events */
        if (this.video) {
          this.video.addEventListener('loadeddata', () => {
            if (this.videoWrap) this.videoWrap.setAttribute('data-loaded', 'true');
          });

          /* We removed the timeupdate listener since progress bars were removed */

          this.video.addEventListener('ended', () => {
            if (this.currentIndex < this.videos.length - 1) {
              this.navigate(1);
            }
          });

          /* Click video → toggle play/pause */
          this.video.addEventListener('click', () => {
            if (this.video.paused) {
              this.video.play().catch(() => {});
            } else {
              this.video.pause();
            }
          });
        }
      }

      disconnectedCallback() {
        if (this._spaCheck) clearInterval(this._spaCheck);
        if (this._onKeydown) document.removeEventListener('keydown', this._onKeydown);
      }

      /* ---------- Open / Close ---------- */

      open() {
        this.setAttribute('open', '');
        this.removeAttribute('hidden');
        document.body.style.overflow = 'hidden';
        document.body.classList.add('reel-modal-open');

        /* Hide the floating widget */
        const widget = document.querySelector('product-reel-widget');
        if (widget) widget.classList.remove('reel-widget--visible');

        /* Load first video */
        this.currentIndex = 0;
        this._loadVideo(0);
        this._updateNav();

        /* Fetch color variants (once) */
        if (!this._variantsFetched) {
          this._fetchColorVariants();
          this._variantsFetched = true;
        }
      }

      close() {
        this.removeAttribute('open');
        document.body.style.overflow = '';
        document.body.classList.remove('reel-modal-open');
        if (this.video) {
          this.video.pause();
          this.video.currentTime = 0;
        }
        /* Re-show widget after a brief delay so the fade-out completes */
        setTimeout(() => {
          const widget = document.querySelector('product-reel-widget');
          if (widget) widget.classList.add('reel-widget--visible');
        }, 350);
      }

      /* ---------- Video Loading ---------- */

      _loadVideo(index) {
        if (!this.video || !this.videos[index]) return;

        if (this.videoWrap) this.videoWrap.setAttribute('data-loaded', 'false');

        this.video.src = this.videos[index];
        this.video.muted = this.isMuted;
        this.video.load();

        const playPromise = this.video.play();
        if (playPromise) {
          playPromise.catch(() => {
            /* Autoplay blocked — mute and retry */
            this.video.muted = true;
            this.isMuted = true;
            this._updateMuteIcon();
            this.video.play().catch(() => {});
          });
        }
      }

      /* ---------- Navigation ---------- */

      navigate(direction) {
        const newIndex = this.currentIndex + direction;
        if (newIndex < 0 || newIndex >= this.videos.length) return;
        this.currentIndex = newIndex;
        this._loadVideo(newIndex);
        this._updateNav();
      }

      _updateNav() {
        if (this.prevBtn) {
          this.prevBtn.disabled = this.currentIndex === 0;
        }
        if (this.nextBtn) {
          this.nextBtn.disabled = this.currentIndex >= this.videos.length - 1;
        }
      }

      /* ---------- Mute ---------- */

      toggleMute() {
        this.isMuted = !this.isMuted;
        if (this.video) this.video.muted = this.isMuted;
        this._updateMuteIcon();
      }

      _updateMuteIcon() {
        if (!this.muteBtn) return;
        const svg = this.isMuted
          ? '<svg viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z"/><line x1="23" y1="9" x2="17" y2="15"/><line x1="17" y1="9" x2="23" y2="15"/></svg>'
          : '<svg viewBox="0 0 24 24"><path d="M11 5L6 9H2v6h4l5 4V5z"/><path d="M19.07 4.93a10 10 0 0 1 0 14.14"/><path d="M15.54 8.46a5 5 0 0 1 0 7.07"/></svg>';
        this.muteBtn.innerHTML = svg;
      }

      /* ---------- Share ---------- */

      async share() {
        const data = this.reelData;
        const shareData = {
          title: data.product.title,
          text: `Check out ${data.product.title} — ${data.product.price}`,
          url: window.location.origin + data.product.url,
        };

        const fallbackCopy = async () => {
          try {
            await navigator.clipboard.writeText(shareData.url);
            this._showShareToast();
          } catch (e) {
            const input = document.createElement('input');
            input.value = shareData.url;
            document.body.appendChild(input);
            input.select();
            document.execCommand('copy');
            input.remove();
            this._showShareToast();
          }
        };

        if (navigator.share) {
          try {
            await navigator.share(shareData);
          } catch (e) {
            if (e.name !== 'AbortError') {
              console.error('[Reels] Share failed:', e);
              await fallbackCopy();
            }
          }
        } else {
          await fallbackCopy();
        }
      }

      _showShareToast() {
        const toast = document.createElement('div');
        toast.textContent = 'Link copied!';
        toast.style.cssText = `
          position:fixed; bottom:2rem; left:50%; transform:translateX(-50%);
          background:#111; color:#fff; padding:0.8rem 2rem; border-radius:2rem;
          font-size:1.3rem; z-index:10000; animation: reel-spin 0.3s ease;
        `;
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 2000);
      }

      /* ---------- Color Variants ---------- */

      async _fetchColorVariants() {
        let handles = new Set();
        
        /* 1. Add manual suggested products */
        if (this.reelData.suggestedProducts && this.reelData.suggestedProducts.length > 0) {
          this.reelData.suggestedProducts.forEach(h => handles.add(h));
        }
        
        /* 2. Add automatic color variants via Search API */
        if (this.reelData.groupTag) {
          try {
            const searchUrl = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent('tag:' + this.reelData.groupTag)}&resources[type]=product`;
            const searchRes = await fetch(searchUrl);
            if (searchRes.ok) {
              const searchData = await searchRes.json();
              if (searchData.resources && searchData.resources.results && searchData.resources.results.products) {
                const products = searchData.resources.results.products;
                const currentHandle = this.reelData.product.handle;
                products.forEach(p => {
                  const urlParts = p.url.split('?')[0].split('/');
                  const handle = urlParts[urlParts.length - 1];
                  if (handle !== currentHandle) {
                    handles.add(handle);
                  }
                });
              }
            }
          } catch (e) {
            console.error('[Reels] Failed to search for group tag:', e);
          }
        }

        if (handles.size === 0) return;

        const rootUrl = window.Shopify?.routes?.root || '/';
        const shopNowText = this.reelData.settings.shopNowText || 'SHOP NOW';

        for (const handle of handles) {
          try {
            const res = await fetch(`${rootUrl}products/${handle}.js`);
            if (!res.ok) continue;
            const prod = await res.json();

            const card = document.createElement('div');
            card.className = 'reel-modal__product-card';
            card.innerHTML = `
              <img class="reel-modal__product-thumb"
                   src="${this._resizeImage(prod.featured_image, '120x')}"
                   alt="${this._escHtml(prod.title)}"
                   loading="lazy">
              <div class="reel-modal__product-info">
                <p class="reel-modal__product-title">${this._escHtml(prod.title)}</p>
                <p class="reel-modal__product-price">${this._formatMoney(prod.price)}</p>
              </div>
              <a href="/products/${handle}" class="reel-modal__shop-btn">${shopNowText}</a>
            `;
            if (this.productsContainer) {
              this.productsContainer.appendChild(card);
            }
          } catch (e) {
            console.error(`[Reels] Failed to fetch variant ${handle}:`, e);
          }
        }
        
        this._triggerCurrencyUpdate();
      }
      
      _triggerCurrencyUpdate() {
        setTimeout(() => {
          try {
            if (typeof window.Currency !== 'undefined' && window.Currency.convertAll) {
              window.Currency.convertAll(window.Shopify.currency.active, document.querySelector('[name=currencies]')?.value || window.Currency.currentCurrency);
            }
            if (typeof window.bacurr !== 'undefined' && window.bacurr.init) {
              window.bacurr.init();
            }
            if (typeof window.doubly !== 'undefined' && window.doubly.update) {
              window.doubly.update();
            }
            document.dispatchEvent(new CustomEvent('currency:update'));
            document.dispatchEvent(new CustomEvent('bucks-currency-update'));
            document.dispatchEvent(new Event('DOMContentLoaded'));
          } catch (e) {
            console.warn('Currency update failed', e);
          }
        }, 50);
      }

      /* ---------- Helpers ---------- */

      _resizeImage(url, size) {
        if (!url) return '';
        return url.replace(/(\.[^.]+)$/, `_${size}$1`);
      }

      _escHtml(str) {
        const div = document.createElement('div');
        div.textContent = str || '';
        return div.innerHTML;
      }

      _formatMoney(cents) {
        const amount = (cents / 100).toFixed(2);
        return `<span class="money">Rs. ${amount.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}</span>`;
      }
    });
  }
})();
